import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Typography,
  Divider,
  Avatar,
  Stack,
  Paper,
  IconButton,
  Box,
} from "@mui/material";
import { useEffect, useState } from "react";
import { Loader } from "../../components/CustomLoader";
import { userServices } from "../../services/AllServices";
import errorHandler from "../../utils/errorHandler";
import { calculateAge, capitalize } from "../../utils/helpers";
import { ArrowBack } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { IMAGE_BASE } from "../../utils/constants";

const ViewUserManagement = ({ id, show, onHide }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selfExclude, setSelfExclude] = useState(null);
  const navigate = useNavigate();

  const userData = async () => {
    try {
      setLoading(true);
      const response = await userServices.getById({ id });
      setData(response.data.data);
      setSelfExclude(response.data.data?.selfExclude || null);
    } catch (error) {
      errorHandler(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) userData();
  }, [id]);

  return (
    <Dialog
      maxWidth="md"
      fullWidth
      open={show}
      onClose={onHide}
      PaperProps={{
        sx: {
          maxHeight: 'calc(100vh - 64px)',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >

      <Paper elevation={6} sx={{ borderRadius: 3, overflow: "hidden", p: 4 }}>

        <IconButton onClick={() => navigate(-1)}>
          <ArrowBack />
        </IconButton>
        <DialogTitle>User Details</DialogTitle>
        {loading ? (
          <Loader />
        ) : (
          <DialogContent
            dividers
            sx={{
              overflowY: 'auto',
              maxHeight: 'calc(100vh - 250px)',
              pr: 2, // Optional: add padding for scrollbar
            }}
          >


            <Grid container spacing={2}>

              <Grid item xs={12}>
                <Box display="flex" justifyContent="center">
                  <img
                    src={IMAGE_BASE + data?.image}
                    alt="User"
                    style={{ maxWidth: "150px", height: "auto", borderRadius: "50%" }}
                  />
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2">First Name</Typography>
                <DialogContentText>{data?.firstname || "-"}</DialogContentText>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2">Last Name</Typography>
                <DialogContentText>{data?.lastname || "-"}</DialogContentText>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2">User Name</Typography>
                <DialogContentText>{data?.username || "-"}</DialogContentText>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2">Email</Typography>
                <DialogContentText>{data?.email || "-"}</DialogContentText>
              </Grid>

              {data?.phoneNumber && (
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2">Phone Number</Typography>
                  <DialogContentText>
                    {data.countryCode} {data.phoneNumber}
                  </DialogContentText>
                </Grid>
              )}

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2">Gender</Typography>
                <DialogContentText>{capitalize(data?.gender || "-")}</DialogContentText>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2">Date of Birth</Typography>
                <DialogContentText>
                  {data?.dob ? new Date(data.dob).toLocaleDateString() : "-"}
                </DialogContentText>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2">Age</Typography>
                <DialogContentText>
                  {data?.dob ? calculateAge(new Date(data.dob).toLocaleDateString()) : "-"}
                </DialogContentText>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2">Country</Typography>
                <DialogContentText>{capitalize(data?.country || "-")}</DialogContentText>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2">Referral Code</Typography>
                <DialogContentText>{data?.referralCode || "-"}</DialogContentText>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2">Signup Bonus Credits</Typography>
                <DialogContentText>{data?.signUpBonusCredits ?? "-"}</DialogContentText>
              </Grid>

              {data?.socialLoginType?.length > 0 && (
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2">Social Login Type</Typography>
                  <DialogContentText>{data.socialLoginType}</DialogContentText>
                </Grid>
              )}

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2">Status</Typography>
                <DialogContentText>
                  {data?.status === 1
                    ? "Active"
                    : data?.status === 0
                      ? "Inactive"
                      : data?.status === 3
                        ? "Self Exclude"
                        : "-"}
                </DialogContentText>
              </Grid>

              {data?.status === 3 && selfExclude && (
                <>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="h6" gutterBottom>
                      Self Exclude Details
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2">Short Break</Typography>
                    <DialogContentText>
                      {selfExclude?.shortBreak ? `${selfExclude.shortBreak} day(s)` : "-"}
                    </DialogContentText>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2">Short Break Until</Typography>
                    <DialogContentText>
                      {selfExclude?.shortBreakUntil
                        ? new Date(selfExclude.shortBreakUntil).toLocaleString()
                        : "-"}
                    </DialogContentText>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2">Self Exclude</Typography>
                    <DialogContentText>
                      {selfExclude?.selfExclude ? `${selfExclude.selfExclude} day(s)` : "-"}
                    </DialogContentText>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2">Self Exclude Until</Typography>
                    <DialogContentText>
                      {selfExclude?.selfExcludeUntil
                        ? new Date(selfExclude.selfExcludeUntil).toLocaleString()
                        : "-"}
                    </DialogContentText>
                  </Grid>
                  <Divider sx={{ my: 2 }} />

                </>
              )}


            </Grid>

            <DialogActions sx={{ mt: 3 }}>
              <Button variant="outlined" onClick={onHide}>
                Close
              </Button>
            </DialogActions>
          </DialogContent>
        )}
      </Paper>
    </Dialog>
  );
};

export default ViewUserManagement;